import {
  Assignment,
  Location,
  Position,
  WorkShift,
  Work,
} from 'typings/common_defs'
import { CompanyWorker } from 'api/worker'
import { addDays, set, startOfWeek } from 'date-fns'
import { pick } from 'ramda'

function createStandardShift() {
  return {
    cancellationPolicy: {
      policyWindows: [
        {
          cutoffTimeHours: 24,
          chargePercentage: 100,
        },
      ],
    },
    isTryout: false,
    locationless: false,
    recommendBackupShift: false,
    unableToAttendWork: [],
    clockInCode: generateClockCode(),
    clockOutCode: generateClockCode(),
    shiftBonuses: [],
  }
}

function generateClockCode() {
  return Math.round(Math.random() * 8999 + 1000)
}

export function createDemoAssignment(
  baseShiftId: number,
  baseWorkId: number,
  baseCalloutId: number,
  location: Location,
  position: Position,
  workers: CompanyWorker[]
): { assignment: Assignment; shifts: WorkShift[] } {
  const now = new Date()
  let orientationDate = startOfWeek(now, { weekStartsOn: 0 })
  if (now.getDay() === 0) {
    orientationDate = addDays(orientationDate, -7)
  }

  const assignment: Assignment = {
    id: 1,
    name: 'Weekday - 3rd Shift',
    position,
    hasOrientation: true,
  }

  const assignedWorkers = workers.slice(0, 10)
  const shifts: WorkShift[] = []

  const createShift = (
    start: Date,
    end: Date,
    isOrientation: boolean,
    index: number
  ) => {
    const status = start < now ? (end < now ? 'paid' : 'started') : 'scheduled'
    const work: Work[] = assignedWorkers.map((w, idx) => ({
      id: baseWorkId + index * 100 + idx,
      startedAt: start.toISOString(),
      completedAt: status === 'paid' ? end.toISOString() : undefined,
      status,
      worker: pick(['id', 'name', 'profilePicUrl'], w),
    }))

    shifts.push({
      ...createStandardShift(),
      id: baseShiftId + index,
      startsAt: start.toISOString(),
      endsAt: end.toISOString(),
      workersNeeded: 10,
      numWorkersFilled: 10,
      numWorkersPending: 0,
      numOpenSpots: 0,
      lunchLength: 0,
      location,
      position,
      supervisors: [],
      work,
      assignment,
      isOrientation,
      payRate: '20',
    })
  }

  const orientationStart = set(orientationDate, {
    hours: 9,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  })
  const orientationEnd = set(orientationDate, {
    hours: 12,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  })
  createShift(orientationStart, orientationEnd, true, 0)

  const WEEKS = 4
  let shiftIndex = 1 // Start after orientation shift
  let foundFirstUpcomingShift = false
  let foundSecondUpcomingShift = false

  for (let week = 0; week < WEEKS; week++) {
    for (let day = 1; day <= 5; day++) {
      const offset = week * 7 + day
      const date = addDays(orientationDate, offset)
      const start = set(date, {
        hours: 19,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      })
      const end = set(addDays(date, 1), {
        hours: 7,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      })
      createShift(start, end, false, shiftIndex)

      // Add callouts to the next two upcoming shifts after today
      const currentShift = shifts[shiftIndex]
      if (currentShift?.work && start > now) {
        if (!foundFirstUpcomingShift) {
          // First upcoming shift gets a callout from first worker
          currentShift.work[0].callouts = [
            {
              id: baseCalloutId + shiftIndex,
              status: 'requested',
              reason:
                'Stomach flu. Reached out to the supervisor and have permission to callout.',
              createdAt: now.toISOString(),
            },
          ]
          foundFirstUpcomingShift = true
        } else if (!foundSecondUpcomingShift) {
          // Second upcoming shift gets a callout from second worker
          currentShift.work[1].callouts = [
            {
              id: baseCalloutId + shiftIndex + 1000,
              status: 'requested',
              reason: 'Family emergency. Need to take care of a sick relative.',
              createdAt: now.toISOString(),
            },
          ]
          foundSecondUpcomingShift = true
        }
      }

      shiftIndex++
    }
  }

  return { assignment, shifts }
}
